/**
 * iconCache.js — Pre-warm Iconify icon cache for offline use.
 *
 * @iconify/react v3 stores fetched icon SVG data in localStorage
 * (keys prefixed `iconify2-`). This module extracts all icon names
 * from a feedback template and triggers `loadIcons()` so they are
 * available on the next offline page refresh.
 */

import { loadIcons, enableCache } from "@iconify/react";

/* Ensure Iconify localStorage caching is on (it's the default, but be explicit) */
enableCache("local");

/**
 * Extract all unique Iconify icon names from a feedback template.
 */
const extractIconNames = (template) => {
    const icons = new Set();
    const collect = (sections) => {
        if (!Array.isArray(sections)) return;
        for (const s of sections) {
            if (s?.icon) icons.add(s.icon);
            if (Array.isArray(s?.sections)) {
                for (const sub of s.sections) {
                    if (sub?.icon) icons.add(sub.icon);
                }
            }
        }
    };
    collect(template?.content?.sections);
    collect(template?.content?.sectionsPositive);
    return [...icons];
};

/**
 * Pre-load all icons from a template so they're cached in localStorage.
 * Safe to call multiple times — Iconify de-duplicates internally.
 *
 * @param {object} template — feedback template response from API
 */
export const preloadTemplateIcons = (template) => {
    const names = extractIconNames(template);
    if (names.length === 0) return;
    loadIcons(names, (loaded, missing) => {
        if (missing?.length) {
            console.warn(
                "[icon-cache] could not load:",
                missing.map((m) => m.name),
            );
        }
    });
};
